import { useState, useEffect, useRef } from 'react';
import fetchWithTenant from '../utils/fetchWithTenant';
import { VideoOff, AlertCircle, Volume2, VolumeX } from 'lucide-react';
import useStore from '../stores/useStore';

const WebRTCPlayer = ({ camera, stream = 'secondary', positionMs = null, muted: externalMuted, onFallback }) => {
  const [status, setStatus] = useState('connecting');
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(externalMuted ?? true);
  
  // Sync with external muted prop
  useEffect(() => {
    if (externalMuted !== undefined) {
      setMuted(externalMuted);
      if (videoRef.current) {
        videoRef.current.muted = externalMuted;
      }
    }
  }, [externalMuted]);
  const [retryCount, setRetryCount] = useState(0);
  const [useMSE, setUseMSE] = useState(false);
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const mediaSourceRef = useRef(null);
  const sourceBufferRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const iceServersRef = useRef([{ urls: 'stun:stun.l.google.com:19302' }]);
  const statusRef = useRef('connecting');
  const retryCountRef = useRef(0);
  const { waveConfig } = useStore();

  const MAX_RETRIES = 2;

  // Keep statusRef in sync with status state (avoids stale closure in ws.onclose)
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // Fetch TURN credentials for external network access
  const fetchTurnCredentials = async () => {
    try {
      const response = await fetch('/api/turn/credentials', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.iceServers) {
          iceServersRef.current = data.iceServers;
          console.log('[TURN] Credentials loaded');
          return true;
        }
      }
      
    } catch (err) {
      console.warn('[TURN] Not available, using STUN only');
    }
    return false;
  };

  const connect = async (tryMSE = false) => {
    if (!camera || !mountedRef.current) return;

    setStatus('connecting');
    setError(null);

    try {
      cleanup(false);
      await fetchTurnCredentials();
      const ticketResponse = await fetchWithTenant('/api/wave/ticket');
      const ticketData = await ticketResponse.json();
      if (!ticketResponse.ok || !ticketData.ticket) {
        throw new Error(ticketData.error || 'Failed to get ticket');
      }
      const ticket = ticketData.ticket;
      
      const cleanCameraId = camera.id.replace(/[{}]/g, '');
      
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      // Try MSE delivery method if SRTP failed
      const deliveryMethod = tryMSE ? '&deliveryMethod=mse' : '';
      const positionParam = positionMs ? `&positionMs=${positionMs}` : '';

      // Tunnel mode: use raw relay proxy; Direct mode: use /wave-ws proxy
      const wsBaseUrl = ticketData.proxyWsUrl
        ? ticketData.proxyWsUrl
        : `${wsProtocol}://${window.location.host}/wave-ws`;
      const proxyAuth = ticketData.proxyAuthToken ? `&_auth=${ticketData.proxyAuthToken}` : '';
      const wsTenantId = localStorage.getItem('currentTenantId');
      const tenantParam = (!ticketData.proxyWsUrl && wsTenantId) ? `&tenantId=${wsTenantId}` : '';
      const wsUrl = `${wsBaseUrl}/rest/v3/devices/${cleanCameraId}/webrtc?stream=${stream}${positionParam}${deliveryMethod}&_ticket=${ticket}${proxyAuth}${tenantParam}`;
      
      console.log(`[${camera.name}] Connecting${tryMSE ? ' (MSE mode)' : ''}...`);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      const connectionTimeout = setTimeout(() => {
        if (wsRef.current?.readyState !== WebSocket.OPEN) {
          console.log(`[${camera.name}] Connection timeout`);
          ws.close();
        }
      }, 10000);

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        if (!mountedRef.current) return;
        console.log(`[${camera.name}] WebSocket connected`);
        setStatus('negotiating');
        setRetryCount(0);
        
        if (tryMSE) {
          setupMSE();
        } else {
          createPeerConnection(iceServersRef.current);
        }
      };

      ws.onmessage = async (event) => {
        if (!mountedRef.current) return;
        
        // Handle binary data (Blob)
        if (event.data instanceof Blob) {
          if (tryMSE) {
            handleMSEData(event.data);
            return;
          }
          // Convert Blob to text for JSON parsing in non-MSE mode
          try {
            const text = await event.data.text();
            const message = JSON.parse(text);
            if (message.sdp && message.sdp.type === 'offer') {
              await handleOffer(message.sdp);
            } else if (message.sdp && message.sdp.type === 'answer') {
              await handleAnswer(message.sdp);
            } else if (message.ice) {
              await handleCandidate(message.ice);
            }
          } catch (err) {
            console.error(`[${camera.name}] Blob parse error:`, err);
          }
          return;
        }
        
        // Handle string data
        try {
          const message = JSON.parse(event.data);
          if (message.sdp && message.sdp.type === 'offer') {
            await handleOffer(message.sdp);
          } else if (message.sdp && message.sdp.type === 'answer') {
            await handleAnswer(message.sdp);
          } else if (message.ice) {
            await handleCandidate(message.ice);
          }
        } catch (err) {
          console.error(`[${camera.name}] Message error:`, err);
        }
      };
      
      ws.onerror = () => {
        clearTimeout(connectionTimeout);
        console.error(`[${camera.name}] WebSocket error`);
      };

      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log(`[${camera.name}] WebSocket closed:`, event.code);
        if (!mountedRef.current) return;
        
        if (statusRef.current !== 'streaming') {
          if (!useMSE && retryCount === 0) {
            // First failure with SRTP, try MSE
            retryCountRef.current++;
          if (retryCountRef.current > MAX_RETRIES) {
            console.log(`[${camera.name}] Max retries (${MAX_RETRIES}) reached, stopping`);
            setStatus('error');
            return;
          }
          console.log(`[${camera.name}] Trying MSE delivery method (attempt ${retryCountRef.current}/${MAX_RETRIES})...`);
            setUseMSE(true);
            setRetryCount(1);
            setTimeout(() => connect(true), 500);
          } else if (retryCount < MAX_RETRIES) {
            const delay = Math.pow(2, retryCount) * 1000 + Math.random() * 500;
            console.log(`[${camera.name}] Retrying in ${Math.round(delay)}ms...`);
            retryTimeoutRef.current = setTimeout(() => {
              if (mountedRef.current) {
                setRetryCount(r => r + 1);
                connect(useMSE);
              }
            }, delay);
          } else {
            console.log(`[${camera.name}] All methods failed, falling back to thumbnail`);
            setError('WebRTC not supported');
            setStatus('fallback');
            onFallback?.();
          }
        }
      };

    } catch (err) {
      console.error(`[${camera.name}] Connect error:`, err);
      if (mountedRef.current) {
        setError(err.message);
        setStatus('error');
      }
    }
  };

  const setupMSE = () => {
    if (!('MediaSource' in window)) {
      console.log(`[${camera.name}] MSE not supported`);
      onFallback?.();
      return;
    }

    const mediaSource = new MediaSource();
    mediaSourceRef.current = mediaSource;
    
    if (videoRef.current) {
      videoRef.current.src = URL.createObjectURL(mediaSource);
    }

    mediaSource.addEventListener('sourceopen', () => {
      try {
        const sourceBuffer = mediaSource.addSourceBuffer('video/webm; codecs="vp8"');
        sourceBufferRef.current = sourceBuffer;
        retryCountRef.current = 0;
          setStatus('streaming');
      } catch (err) {
        console.error(`[${camera.name}] MSE setup error:`, err);
      }
    });
  };

  const handleMSEData = async (blob) => {
    if (!sourceBufferRef.current || sourceBufferRef.current.updating) return;
    
    try {
      const buffer = await blob.arrayBuffer();
      sourceBufferRef.current.appendBuffer(buffer);
    } catch (err) {
      // Buffer might be full or updating
    }
  };

  const createPeerConnection = (servers) => {
    const pc = new RTCPeerConnection({
      iceServers: servers || [{ urls: 'stun:stun.l.google.com:19302' }]
    });
    pcRef.current = pc;

    pc.ontrack = (event) => {
      console.log(`[${camera.name}] Track received:`, event.track.kind);
      if (videoRef.current && event.streams[0] && mountedRef.current) {
        videoRef.current.srcObject = event.streams[0];
        setStatus('streaming');
        
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          ice: {
            candidate: event.candidate.candidate,
            sdpMid: event.candidate.sdpMid,
            sdpMLineIndex: event.candidate.sdpMLineIndex
          }
        }));
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log(`[${camera.name}] ICE:`, pc.iceConnectionState);
      if (pc.iceConnectionState === 'failed' && mountedRef.current) {
        // Try MSE before giving up
        if (!useMSE) {
          console.log(`[${camera.name}] ICE failed, trying MSE...`);
          setUseMSE(true);
          cleanup(false);
          connect(true);
        } else {
          setError('Connection failed');
          setStatus('fallback');
          onFallback?.();
        }
      }
    };
  };

  const handleOffer = async (sdp) => {
    const pc = pcRef.current;
    if (!pc || !mountedRef.current) return;

    try {
      await pc.setRemoteDescription(new RTCSessionDescription({
        type: 'offer',
        sdp: sdp.sdp
      }));

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          sdp: {
            type: 'answer',
            sdp: answer.sdp
          }
        }));
      }
    } catch (err) {
      console.error(`[${camera.name}] Offer error:`, err);
      if (mountedRef.current) {
        setError('Negotiation failed');
        setStatus('fallback');
        onFallback?.();
      }
    }
  };

  const handleAnswer = async (sdp) => {
    const pc = pcRef.current;
    if (!pc) return;

    try {
      await pc.setRemoteDescription(new RTCSessionDescription({
        type: 'answer',
        sdp: sdp.sdp
      }));
    } catch (err) {
      console.error(`[${camera.name}] Answer error:`, err);
    }
  };

  const handleCandidate = async (ice) => {
    const pc = pcRef.current;
    if (!pc || !ice.candidate) return;

    try {
      await pc.addIceCandidate(new RTCIceCandidate({
        candidate: ice.candidate,
        sdpMid: ice.sdpMid,
        sdpMLineIndex: ice.sdpMLineIndex
      }));
    } catch (err) {
      // Ignore
    }
  };

  const cleanup = (clearRetry = true) => {
    if (clearRetry && retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (mediaSourceRef.current) {
      mediaSourceRef.current = null;
    }
    if (sourceBufferRef.current) {
      sourceBufferRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.src = '';
    }
  };

  const lastPositionRef = useRef(positionMs);
  
  useEffect(() => {
    mountedRef.current = true;
    setUseMSE(false);
    connect(false);

    return () => {
      mountedRef.current = false;
      cleanup(true);
    };
  }, [camera?.id, waveConfig?.host]);
  
  // Reconnect when user seeks (not when time naturally advances)
  const playStartTimeRef = useRef(null);
  const playStartPositionRef = useRef(null);
  
  useEffect(() => {
    if (positionMs === null) {
      // Live mode - reset tracking
      lastPositionRef.current = null;
      playStartTimeRef.current = null;
      playStartPositionRef.current = null;
      return;
    }
    
    // First time entering archive mode or after a reset
    if (playStartTimeRef.current === null) {
      playStartTimeRef.current = Date.now();
      playStartPositionRef.current = positionMs;
      lastPositionRef.current = positionMs;
      return;
    }
    
    // Calculate where we expect the position to be based on elapsed real time
    const elapsedRealTime = Date.now() - playStartTimeRef.current;
    const expectedPosition = playStartPositionRef.current + elapsedRealTime;
    
    // If positionMs is far from expected, user has seeked - reconnect
    // Allow 15 second tolerance for natural drift
    if (Math.abs(positionMs - expectedPosition) > 15000) {
      console.log('[WebRTC] User seeked, reconnecting...', { 
        expected: expectedPosition, 
        actual: positionMs,
        drift: positionMs - expectedPosition 
      });
      // Reset tracking for new playback position
      playStartTimeRef.current = Date.now();
      playStartPositionRef.current = positionMs;
      lastPositionRef.current = positionMs;
      cleanup(true);
      setUseMSE(false);
      setRetryCount(0);
      connect(false);
    }
  }, [positionMs]);

  const toggleMute = (e) => {
    e.stopPropagation();
    setMuted(!muted);
    if (videoRef.current) {
      videoRef.current.muted = !muted;
    }
  };

  const handleRetry = (e) => {
    e.stopPropagation();
    setRetryCount(0);
    setUseMSE(false);
    connect(false);
  };

  if (status === 'fallback') {
    return null;
  }

  return (
    <div className="webrtc-player">
      {(status === 'connecting' || status === 'negotiating') && (
        <div className="camera-placeholder">
          <div className="loading-spinner" />
          <span>{status === 'connecting' ? 'Connecting...' : 'Negotiating...'}</span>
        </div>
      )}

      {status === 'error' && (
        <div className="camera-placeholder">
          <AlertCircle size={32} />
          <span>{error || 'Connection failed'}</span>
          <button className="btn btn-sm btn-secondary" onClick={handleRetry}>
            Retry
          </button>
        </div>
      )}

      {!camera && (
        <div className="camera-placeholder">
          <VideoOff size={32} />
          <span>No camera selected</span>
        </div>
      )}

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={muted}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: status === 'streaming' ? 'block' : 'none'
        }}
      />

      {status === 'streaming' && (
        <button
          className="btn-icon btn-sm"
          onClick={toggleMute}
          style={{
            position: 'absolute',
            bottom: '10px',
            right: '10px',
            background: 'rgba(0,0,0,0.6)',
            border: 'none'
          }}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      )}
    </div>
  );
};

export default WebRTCPlayer;
